import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { DrawerState } from "../types/DrawerState";
import GlobalDrawer from "../components/GlobalDrawer/GlobalDrawer";

export type DrawerContextProps = {
  drawerState: DrawerState | null;
  setDrawerState: (drawerState: DrawerState | null) => Promise<boolean>;
  closeDrawer: () => Promise<boolean>;
  registerDraftSave: (flush: () => Promise<void>) => () => void;
  flushDraftSave: () => Promise<void>;
  setDrawerTitle: Dispatch<SetStateAction<string>>;
  drawerContentNode: HTMLDivElement | null;
  drawerActionsNode: HTMLDivElement | null;
  drawerHeaderNode: HTMLDivElement | null;
};

const DrawerProviderContext = createContext<DrawerContextProps | null>(null);

export const DrawerProvider = ({ children }: { children: ReactNode }) => {
  const [drawerState, setDrawerState] = useState<DrawerState | null>(null);
  const [drawerTitle, setDrawerTitle] = useState<string>("");
  const [drawerContentNode, setDrawerContentNode] = useState<HTMLDivElement | null>(null);
  const [drawerActionsNode, setDrawerActionsNode] = useState<HTMLDivElement | null>(null);
  const [drawerHeaderNode, setDrawerHeaderNode] = useState<HTMLDivElement | null>(null);
  const [drawerKey, setDrawerKey] = useState("");
  const draftSaveRef = useRef<(() => Promise<void>) | null>(null);
  const transitionRef = useRef(0);

  const registerDraftSave = (flush: () => Promise<void>) => {
    draftSaveRef.current = flush;
    return () => {
      if (draftSaveRef.current === flush) {
        draftSaveRef.current = null;
      }
    };
  };

  const flushDraftSave = async () => {
    await draftSaveRef.current?.();
  };

  const setDrawerStateExtended = async (nextDrawerState: DrawerState | null): Promise<boolean> => {
    const transition = ++transitionRef.current;
    if (draftSaveRef.current) {
      try {
        await flushDraftSave();
      } catch {
        enqueueSnackbar("Die Änderungen konnten nicht gespeichert werden. Bitte erneut versuchen.", { variant: "error" });
        return false;
      }
    }
    if (transition !== transitionRef.current) {
      return false;
    }
    // Fires the outgoing drawer's onClose on every close or replace, never on the initial open.
    if (drawerState && drawerState !== nextDrawerState) {
      drawerState.onClose?.();
    }
    setDrawerState(nextDrawerState);
    setDrawerKey(crypto.randomUUID());
    return true;
  };

  const closeDrawer = () => {
    return setDrawerStateExtended(null);
  };

  return (
    <DrawerProviderContext.Provider
      value={{
        drawerState,
        setDrawerState: setDrawerStateExtended,
        closeDrawer,
        registerDraftSave,
        flushDraftSave,
        drawerActionsNode,
        drawerContentNode,
        drawerHeaderNode,
        setDrawerTitle,
      }}
    >
      {children}
      <GlobalDrawer
        drawerTitle={drawerTitle}
        setDrawerContentNode={setDrawerContentNode}
        setDrawerActionsNode={setDrawerActionsNode}
        setDrawerHeaderNode={setDrawerHeaderNode}
        key={drawerKey}
      />
    </DrawerProviderContext.Provider>
  );
};

export const useDrawer = () => {
  const context = useContext(DrawerProviderContext);
  if (!context) {
    throw new Error("useDrawer must be used within a DrawerProvider");
  }
  return context;
};

export const useOptionalDrawer = () => {
  const context = useContext(DrawerProviderContext);
  return context || null;
};
